import axios, { AxiosError } from 'axios';

import { getBearer, isDesktop } from './desktop-token';
import type { HostSession, Workspace } from '../types/terminal';

export const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '',
    // Send better-auth session cookies on every request.
    withCredentials: true,
});

// In the desktop app there is no session cookie — auth rides a bearer token
// (see desktop-token.ts). Attach it to every request; the browser path is a
// no-op (isDesktop() is false) and keeps using cookies.
api.interceptors.request.use((config) => {
    const token = getBearer();
    if (isDesktop() && token) {
        config.headers.set('Authorization', `Bearer ${token}`);
        config.withCredentials = false;
    }
    return config;
});

export type AuthMethod = 'key' | 'password' | 'agent';

// Desktop transport policy: 'direct' (default) SSHes straight from the
// desktop app; 'auto' falls back to relay when direct fails; 'relay' forces
// the api gateway (and blocks credential release server-side). Web always
// relays regardless.
export type ConnectionMode = 'relay' | 'direct' | 'auto';

export interface ServerHost {
    id: string;
    name: string;
    hostname: string;
    port: number;
    sshUser: string;
    authMethod: AuthMethod;
    connectionMode: ConnectionMode;
    os: string | null;
    tags: string[];
    note: string | null;
    sharedWith: string[];
    isDeleted: boolean;
    deletedAt: string | null;
    lastSeenAt: string | null;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string | null;
    userId: string;
}

export interface HostCredentialFields {
    privateKey?: string;
    passphrase?: string;
    password?: string;
}

export interface CreateHostBody extends HostCredentialFields {
    name: string;
    hostname: string;
    port?: number;
    sshUser: string;
    authMethod: AuthMethod;
    connectionMode?: ConnectionMode;
    os?: string;
    tags?: string[];
    note?: string;
    sharedWith?: string[];
}

export interface TestHostBody extends HostCredentialFields {
    hostname: string;
    port?: number;
    sshUser: string;
    authMethod: AuthMethod;
}

export interface SshTestResult {
    ok: boolean;
    error?: string;
    code?: string;
}

export const hostsApi = {
    list: () => api.get<{ hosts: ServerHost[] }>('/api/hosts'),
    get: (id: string) => api.get<{ host: ServerHost }>(`/api/hosts/${id}`),
    create: (body: CreateHostBody) =>
        api.post<{ host: ServerHost }>('/api/hosts', body),
    update: (id: string, body: Partial<CreateHostBody>) =>
        api.patch<{ host: ServerHost }>(`/api/hosts/${id}`, body),
    delete: (id: string) => api.delete(`/api/hosts/${id}`),
    test: (body: TestHostBody) =>
        api.post<SshTestResult>('/api/hosts/test', body),
};

export interface ActiveTerminalConnection {
    sessionId: string;
    hostId: string;
    hostname: string;
    sshUser: string;
    startedAt: string;
    cols: number;
    rows: number;
    bytesIn: number;
    bytesOut: number;
}

export const terminalApi = {
    connections: () =>
        api.get<{ connections: ActiveTerminalConnection[] }>(
            '/api/terminal/connections',
        ),
    disconnect: (sessionId: string) =>
        api.delete(`/api/terminal/connections/${sessionId}`),

    // Convenience: tear down every live session for a given host. Used by
    // the sidebar's host-row Disconnect action (the sidebar doesn't hold
    // a sessionId itself — it has to look them up).
    disconnectHost: async (hostId: string): Promise<number> => {
        const { data } = await api.get<{
            connections: ActiveTerminalConnection[];
        }>('/api/terminal/connections');
        const matches = data.connections.filter((c) => c.hostId === hostId);
        await Promise.all(
            matches.map((c) =>
                api.delete(`/api/terminal/connections/${c.sessionId}`),
            ),
        );
        return matches.length;
    },

    // Last activity per host, for the whole sidebar in one call (the row shows
    // "when was I last in here" when nothing is live).
    hostsLastUsed: () =>
        api.get<{ hosts: { hostId: string; lastUsedAt: string | null }[] }>(
            '/api/terminal/hosts/last-used',
        ),

    // ─── Per-host session manager ──────────────────────────────────────────
    hostSessions: (hostId: string) =>
        api.get<{ sessions: HostSession[] }>(
            `/api/terminal/hosts/${hostId}/sessions`,
        ),
    createHostSession: (hostId: string, body?: { id?: string; label?: string }) =>
        api.post<{ session: HostSession }>(
            `/api/terminal/hosts/${hostId}/sessions`,
            body ?? {},
        ),
    renameHostSession: (hostId: string, sessionId: string, label: string) =>
        api.patch<{ session: HostSession }>(
            `/api/terminal/hosts/${hostId}/sessions/${sessionId}`,
            { label },
        ),
    deleteHostSession: (hostId: string, sessionId: string) =>
        api.delete(`/api/terminal/hosts/${hostId}/sessions/${sessionId}`),

    // ─── Cross-device workspace layout ─────────────────────────────────────
    workspace: () =>
        api.get<{ workspace: Workspace | null }>('/api/terminal/workspace'),
    saveWorkspace: (layout: Workspace) =>
        api.put('/api/terminal/workspace', { layout }),
};

// ─── SFTP file browser ──────────────────────────────────────────────────────

export interface SftpEntry {
    name: string;
    type: 'file' | 'dir' | 'symlink' | 'other';
    size: number;
    /** ms epoch (0 when the server didn't report one). */
    mtime: number;
    mode: number;
}

// Mirrors the server's pre-parse multipart cap so oversized uploads fail
// instantly client-side instead of after a long transfer.
export const SFTP_MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

// Mirrors the server's paste-image cap (images only — far below the general
// upload cap above).
export const PASTE_IMAGE_MAX_BYTES = 25 * 1024 * 1024;

export const sftpApi = {
    // path omitted/null → the server resolves the user's home directory and
    // echoes the normalized absolute path back.
    list: (hostId: string, path?: string | null) =>
        api.get<{ path: string; entries: SftpEntry[] }>(
            `/api/sftp/hosts/${hostId}/list`,
            { params: path ? { path } : {} },
        ),

    upload: (
        hostId: string,
        path: string,
        files: File[],
        onProgress?: (pct: number) => void,
    ) => {
        const form = new FormData();
        form.append('path', path);
        for (const f of files) form.append('files', f);
        return api.post<{
            uploaded: Array<{ name: string; size: number }>;
            errors: Array<{ name: string; error: string }>;
        }>(`/api/sftp/hosts/${hostId}/upload`, form, {
            onUploadProgress: (e) => {
                if (onProgress && e.total) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            },
        });
    },

    // Save one clipboard image to the session host's paste dir; the server
    // picks the temp path and echoes it back for injection into the terminal.
    // The extension comes from the blob's MIME type — the part name is cosmetic.
    pasteImage: (hostId: string, blob: Blob) => {
        const form = new FormData();
        form.append('file', blob, 'clipboard');
        return api.post<{ path: string }>(
            `/api/sftp/hosts/${hostId}/paste-image`,
            form,
        );
    },

    // Downloads ride a browser-native top-level GET (streamed, no buffering):
    // the web build sends session cookies; the desktop build has no cookie, so
    // the bearer token travels as `?t=` and the server promotes it to an
    // Authorization header — the same trick as the terminal's wsUrl().
    // (Fallback if anchor downloads ever misbehave: axios with
    // responseType 'blob' + an object URL — buffers in memory, last resort.)
    downloadUrl: (hostId: string, path: string): string => {
        const base = (
            (import.meta.env.VITE_API_URL as string | undefined) ||
            window.location.origin
        ).replace(/\/$/, '');
        const url = new URL(`${base}/api/sftp/hosts/${hostId}/download`);
        url.searchParams.set('path', path);
        const token = getBearer();
        if (isDesktop() && token) url.searchParams.set('t', token);
        return url.toString();
    },
};

// Pulls the API's `{ error, code }` shape out of an AxiosError so the caller
// can display a useful message regardless of HTTP status.
export const apiErrorMessage = (err: unknown): { message: string; code?: string } => {
    if (err instanceof AxiosError) {
        const data = err.response?.data as { error?: string; code?: string } | undefined;
        if (data?.error) return { message: data.error, code: data.code };
        if (err.message) return { message: err.message };
    }
    if (err instanceof Error) return { message: err.message };
    return { message: 'unknown error' };
};
